import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle, Database, Layout, Palette, ShieldCheck, Activity, Loader2, Save, AlertTriangle } from 'lucide-react';
import type { WidgetDefinition, DomainCategory, WidgetPresentationType, MappingConfig, AllowedInternalRoutes, WidgetFieldMapping } from '../../types';
import { createWidget, updateWidget, getAllowedInternalRoutes } from '../../api/dashboardCatalog';
import { useDashboardContext } from '../../useDashboardContext';
import { useDashboardTranslation } from '../../i18n';
import { type EconomicIndicator } from '../../utils/indicatorsCatalog';
import { INITIAL_FORM_DATA } from './types';
import type { WizardFormData } from './types';
import { Step1DataSource } from './Step1DataSource';
import { Step2FieldMapping } from './Step2FieldMapping';
import { Step3PresentationType } from './Step3PresentationType';
import { Step4Appearance } from './Step4Appearance';
import { Step5Permissions, type RepartoInicial } from './Step5Permissions';
import {
  normalizeCategories,
  verticalDeLaCategoria,
  type DashboardCategoryInput,
  type DashboardCategoryOption,
} from './categories';
import type { WidgetAccessPerson } from '../../access/types';

interface WidgetBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (widget: WidgetDefinition) => void;
  mode?: 'create' | 'edit';
  editWidget?: WidgetDefinition;
  isAdmin?: boolean;
  /**
   * Capacidad de cambiar la EXPOSICION de un widget: a quien alcanza (`es_privado`,
   * `roles_permitidos`) y con que autoridad se pide su dato (`is_internal`). Es lo que gobierna el
   * paso de permisos del asistente.
   *
   * Se separa de `isAdmin` porque el backend la exige por separado, y en modo edicion manda lo que
   * diga el propio widget (`permissions.can_publish`). Sin valor cae a `isAdmin`, que es el
   * comportamiento historico.
   */
  canPublishWidgets?: boolean;
  showExternalEndpoints?: boolean;
  /**
   * 🔴 **`availableRoles` se RETIRÓ el 2026-09-14 (F3).** El paso de permisos ya no pregunta la
   * vertical: la determina la categoría. El tipo sigue exportado porque el **diálogo de acceso** lo
   * usa para ofrecer «que lo vea todo ventas» como sujeto de un grant — que es repartir algo que ya
   * existe, otro acto —, pero el Constructor no lo recibe.
   *
   * El vocabulario de categorías trae ahora la **pareja** categoría→vertical. Ver
   * `DashboardCategoryOption`.
   */
  availableCategories?: DashboardCategoryInput[];

  /**
   * 🔴 **F4.4 — «ofrece abrir el diálogo de acceso».** Se llama cuando el indicador se creó pero el
   * reparto quedó a medias, con el widget ya creado.
   *
   * <para>Sin esto el aviso sería un callejón: se le dice a alguien que su indicador quedó
   * restringido y sin nadie dentro, y se le deja buscarlo en el catálogo. El host conecta esto con
   * su diálogo de acceso; si no lo declara, el asistente solo cierra.</para>
   */
  onNeedsAccessDialog?: (widget: WidgetDefinition) => void;
}

interface ProxyTestResponse {
  success: boolean;
  data?: Record<string, unknown>[];
  fields?: string[];
  numericFields?: string[];
  error?: string;
  total_records?: number;
}

/**
 * Flujo CATÁLOGO (3 pasos): Fuente → Apariencia → Permisos
 * Salta Presentación (siempre KPI) y Mapeo (automático del catálogo).
 */
const CATALOG_STEPS = [
  { id: 'dataSource',  title: 'Fuente de Datos', icon: Database   },
  { id: 'appearance',  title: 'Apariencia',       icon: Palette    },
  { id: 'permissions', title: 'Permisos',          icon: ShieldCheck},
];

/**
 * Flujo MANUAL (5 pasos): Fuente → Presentación → Mapeo → Apariencia → Permisos
 * Flujo completo para APIs arbitrarias.
 */
const MANUAL_STEPS = [
  { id: 'dataSource',   title: 'Fuente de Datos', icon: Database   },
  { id: 'presentation', title: 'Presentación',    icon: Layout     },
  { id: 'mapping',      title: 'Mapeo',            icon: Activity   },
  { id: 'appearance',   title: 'Apariencia',       icon: Palette    },
  { id: 'permissions',  title: 'Permisos',          icon: ShieldCheck},
];

/**
 * Genera un field_mapping simple (Record<string,string>) a partir de MappingConfig
 * para mantener compatibilidad con applyFieldMapping() en useWidgetFetch.
 */
function buildFieldMapping(config: MappingConfig | null): Record<string, string> {
  if (!config) return {};
  const fm: Record<string, string> = {};

  switch (config.type) {
    case 'kpi':
      if (config.valueField !== 'valor') fm['valor'] = config.valueField;
      if (config.labelField && config.labelField !== 'etiqueta') fm['etiqueta'] = config.labelField;
      break;
    case 'chart_bar_vertical':
      if (config.xField !== 'name') fm['name'] = config.xField;
      if (config.yField !== 'value') fm['value'] = config.yField;
      break;
    case 'chart_line':
      if (config.xField !== 'name') fm['name'] = config.xField;
      if (config.yField !== 'value') fm['value'] = config.yField;
      break;
    case 'chart_donut':
      if (config.categoryField !== 'name') fm['name'] = config.categoryField;
      if (config.valueField !== 'value') fm['value'] = config.valueField;
      break;
    case 'table':
      // Table no necesita field_mapping para renderizar
      break;
  }

  return fm;
}

/** Valida si el mappingConfig tiene los campos obligatorios completos */
function isMappingValid(config: MappingConfig | null): boolean {
  if (!config) return false;
  switch (config.type) {
    case 'kpi': return !!config.valueField;
    case 'chart_bar_vertical': return !!config.xField && !!config.yField;
    case 'chart_line': return !!config.xField && !!config.yField;
    case 'chart_donut': return !!config.categoryField && !!config.valueField;
    case 'table': return config.visibleColumns.length > 0;
    default: return false;
  }
}

export const WidgetBuilder: React.FC<WidgetBuilderProps> = ({
  isOpen,
  onClose,
  onSuccess,
  mode = 'create',
  editWidget,
  isAdmin = false,
  canPublishWidgets,
  showExternalEndpoints = false,
  availableCategories,
  onNeedsAccessDialog,
}) => {
  const { fetcher, accessAdapter } = useDashboardContext();
  const { t } = useDashboardTranslation();
  const isEditMode = mode === 'edit' && !!editWidget;

  const [step, setStep] = useState(1);
  const [isTesting, setIsTesting] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [jsonPreview, setJsonPreview] = useState<Record<string, unknown>[] | null>(null);
  const [mappingConfig, setMappingConfig] = useState<MappingConfig | null>(null);
  const [formData, setFormData] = useState<WizardFormData>({ ...INITIAL_FORM_DATA, is_internal: false });

  // ── F4 · Repartir al crear ───────────────────────────────────────────────────────────────────
  //
  // ⚠️ Y hay que decirlo donde se implementa, porque se vende mal: con el Constructor restringido a
  // administradores (F1), esto es COMODIDAD, NO SEGURIDAD. Ese administrador ya podía hacerlo en dos
  // pantallas. Se hace porque cuatro pasos para una intención —crear, cerrar, abrir el diálogo,
  // repartir— es una mala pantalla, no porque cierre ningún hueco.
  const [reparto, setReparto] = useState<RepartoInicial>('module');
  const [personas, setPersonas] = useState<WidgetAccessPerson[]>([]);
  const [avisoReparto, setAvisoReparto] = useState<string | null>(null);
  /**
   * El indicador que SÍ se creó cuando el reparto quedó a medias. Se guarda porque `onSuccess` —que
   * es lo que el host usa para refrescar y CERRAR el asistente— no se llama todavía: cerrar con el
   * aviso puesto sería dejarlo sin sitio donde leerse.
   */
  const [creadoAMedias, setCreadoAMedias] = useState<WidgetDefinition | null>(null);

  // Flujo dinámico: catálogo=3 pasos, manual=5 pasos
  // En modo edición siempre flujo manual (el widget ya tiene URL configurada)
  const isCatalogMode = !isEditMode && formData.catalog_mode === 'catalogo';
  const baseSteps = isCatalogMode ? CATALOG_STEPS : MANUAL_STEPS;

  /**
   * Si esta persona puede mover la exposicion DE ESTE widget. En edicion manda lo que dijo el
   * servidor sobre la fila; al crear, la capacidad global; y sin ninguna de las dos, `isAdmin`, que
   * es lo que gobernaba antes.
   *
   * Gobierna el paso de permisos y los tres campos que se mandan: sin esto, el asistente ofrecia el
   * paso a quien el API iba a rechazar -o, peor, mandaba `es_privado: true` a espaldas de quien
   * editaba, volviendo privado un widget de modulo y quitandoselo a todo el mundo-.
   */
  const puedeExponer = isEditMode
    ? (editWidget?.permissions?.can_publish ?? canPublishWidgets ?? isAdmin)
    : (canPublishWidgets ?? isAdmin);

  /**
   * 🔴 **La vertical NO se pregunta: se deriva de la categoría.** Es F3, y lo que cierra es un
   * defecto que no daba ningún error: categoría y vertical eran dos campos distintos que
   * significaban lo mismo, y nada impedía archivar un indicador en «Ventas» y marcarlo de «compras».
   *
   * `null` cuando el host no publicó la pareja para esa categoría. Ver `verticalDeLaCategoria`.
   */
  const categoriasDelHost = React.useMemo(
    () => normalizeCategories(availableCategories),
    [availableCategories],
  );
  const vertical: DashboardCategoryOption | null = verticalDeLaCategoria(
    categoriasDelHost,
    formData.categoria,
  );

  /**
   * Repartir al crear exige las DOS operaciones del host: restringir (`savePolicy`) y conceder
   * (`grant`). Con una sola, el widget se crearía público y el segundo acto no llegaría — que es
   * justo el final que 4.4 existe para no producir en silencio.
   */
  const puedeRepartirAlCrear =
    !isEditMode && !!accessAdapter?.savePolicy && !!accessAdapter?.grant;

  /**
   * 🔴 **Memoizado, y no `accessAdapter.searchPeople.bind(...)` en el JSX.** `bind` devuelve una
   * función NUEVA en cada render: el buscador del paso 5 la recibía como prop, su efecto se
   * remontaba en cada pasada y el debounce se cancelaba antes de lanzarse — **el spinner se quedaba
   * girando y no aparecía ni un resultado**. El hijo ya se defiende de eso, pero pasar una identidad
   * estable es lo correcto aquí: no es una optimización, es no romperlo desde fuera.
   */
  const buscarPersonas = React.useMemo(
    () =>
      accessAdapter?.searchPeople
        ? (q: string) => accessAdapter.searchPeople!(q)
        : undefined,
    [accessAdapter],
  );

  const steps = puedeExponer ? baseSteps : baseSteps.filter(s => s.id !== 'permissions');
  const maxStep = steps.length;
  // ID del paso actual (para render y validación)
  const currentStepId = steps[step - 1]?.id ?? 'dataSource';
  const [totalRecords, setTotalRecords] = useState(0);
  const [allowedRoutes, setAllowedRoutes] = useState<AllowedInternalRoutes | undefined>();
  // Estado del indicador seleccionado del catálogo
  const [selectedIndicator, setSelectedIndicator] = useState<EconomicIndicator | null>(null);

  // Cargar config del whitelist al abrir el Builder
  useEffect(() => {
    if (isOpen) {
      getAllowedInternalRoutes(fetcher)
        .then(setAllowedRoutes)
        .catch(() => setAllowedRoutes(undefined));
    }
  }, [isOpen, fetcher]);

  // Precargar datos cuando se abre en modo edición
  useEffect(() => {
    if (isEditMode && editWidget) {
      setFormData({
        nombre: editWidget.nombre,
        descripcion: editWidget.descripcion,
        categoria: editWidget.categoria as DomainCategory,
        tipo: editWidget.tipo as WidgetPresentationType,
        icono: editWidget.icono,
        roles_permitidos: editWidget.roles_permitidos,
        col_min: editWidget.col_min,
        col_max: editWidget.col_max,
        cols_default: editWidget.cols_default,
        altura_default: editWidget.altura_default,
        activo: editWidget.activo,
        endpoint_url: editWidget.endpoint_url ?? '',
        endpoint_method: (editWidget.endpoint_method as 'GET' | 'POST') ?? 'GET',
        endpoint_headers: editWidget.endpoint_headers ?? {},
        endpoint_params: editWidget.endpoint_params ?? {},
        refresh_policy: editWidget.refresh_policy ?? 300,
        es_privado: editWidget.es_privado ?? false,
        is_internal: editWidget.is_internal ?? true,
        // Catálogo — modo manual por defecto al editar (el widget ya tenía URL)
        catalog_mode: 'manual',
        catalog_indicator_id: undefined,
        catalog_api_key: '',
        campo_valor: '',
        campo_fecha: '',
      });
      // Restaurar MappingConfig desde config_schema si existe
      if (editWidget.config_schema && typeof editWidget.config_schema === 'object' && 'type' in editWidget.config_schema) {
        setMappingConfig(editWidget.config_schema as unknown as MappingConfig);
      }
      // Marcar test como exitoso si ya tiene URL configurada
      if (editWidget.endpoint_url) {
        setTestSuccess(true);
      }
      setStep(1);
    }
  }, [isEditMode, editWidget]);

  const updateForm = (key: keyof WizardFormData, value: unknown) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  // Auto-detectar si una URL parece ser interna (mismo host, localhost o URL relativa)
  /**
   * 🔴 `is_internal` EFECTIVO, resuelto en el momento de guardar y no antes.
   *
   * <para>Existe porque el valor del formulario puede no estar puesto: en modo catálogo se pone al
   * elegir el indicador, y en modo manual dentro de `handleTestConnection`. Un camino que no pase
   * por ninguno de los dos —o el estado de React todavía sin aplicar en este render— dejaba
   * `undefined`, y el `?? true` de abajo lo convertía en INTERNO. Un widget externo declarado interno
   * no falla al crearse: falla al pedir su dato, lejos de donde se causó.</para>
   *
   * <para>Aquí se decide con lo que de verdad manda: si viene del catálogo es externo, y si no, se
   * deriva de la URL. Es una función pura sobre los datos del formulario, así que no depende de que
   * ningún efecto haya corrido.</para>
   */
  const resolverIsInternal = (): boolean => {
    if (formData.catalog_indicator_id) return false;
    if (formData.is_internal !== undefined) return formData.is_internal;
    return detectIsInternal(formData.endpoint_url ?? '');
  };

  const detectIsInternal = (url: string): boolean => {
    const u = url.toLowerCase().trim();
    if (u.startsWith('/') || !u.includes('://')) return true; // URL relativa
    try {
      const parsed = new URL(u);
      const currentHost = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
      return (
        parsed.hostname === 'localhost' ||
        parsed.hostname === '127.0.0.1' ||
        (currentHost !== '' && parsed.hostname === currentHost)
      );
    } catch {
      return false;
    }
  };

  // ───── Paso 2: Test de conexión via proxy/test ─────
  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestSuccess(false);
    setTestError(null);
    setJsonPreview(null);

    // Auto-detectar is_internal solo en modo create (no sobrescribir decision del usuario en edit)
    if (!isEditMode) {
      const detected = detectIsInternal(formData.endpoint_url);
      if (detected !== formData.is_internal) {
        setFormData(prev => ({ ...prev, is_internal: detected }));
      }
    }

    try {
      if (fetcher) {
        const result = await fetcher<ProxyTestResponse>('/api/dashboard/proxy/test', {
          method: 'POST',
          body: {
            url: formData.endpoint_url,
            method: formData.endpoint_method,
            headers: Object.keys(formData.endpoint_headers ?? {}).length > 0
              ? formData.endpoint_headers
              : undefined,
            params: formData.endpoint_method === 'POST' && Object.keys(formData.endpoint_params ?? {}).length > 0
              ? formData.endpoint_params
              : undefined,
          },
        });

        if (result.success && result.data && result.data.length > 0) {
          setTestSuccess(true);
          setJsonPreview(result.data);
          setTotalRecords(result.total_records ?? result.data.length);
        } else {
          setTestError(result.error || 'El endpoint no devolvió datos válidos.');
        }
      } else {
        await new Promise(r => setTimeout(r, 800));
        setTestSuccess(true);
        setJsonPreview([
          { nombre: 'Ene', valor: 12400, meta: 15000 },
          { nombre: 'Feb', valor: 18200, meta: 15000 },
          { nombre: 'Mar', valor: 9800, meta: 15000 },
        ]);
      }
    } catch (error) {
      setTestError(error instanceof Error ? error.message : 'Error de conexión desconocido');
    } finally {
      setIsTesting(false);
    }
  };

  // ───── Guardar/Actualizar widget ─────
  /** Si esta creación tiene que repartir a personas después de crear. */
  const repartoAPersonas =
    !isEditMode && puedeExponer && !!vertical && !formData.es_privado
    && reparto === 'people' && puedeRepartirAlCrear;

  /**
   * 🔴 **Los dos actos que van DESPUÉS de crear, y el mensaje cuando alguno no ocurre.**
   *
   * Devuelve `null` si todo salió, o el texto de lo que quedó a medias. **No lanza**: el widget ya
   * existe, así que tratar esto como un error del guardado diría «no se pudo crear» sobre algo que sí
   * se creó — y la persona volvería a intentarlo y tendría dos.
   *
   * ⚠️ **El peor final tiene nombre propio:** un indicador creado, restringido y **sin nadie dentro**
   * — invisible hasta para su autor, y sin un solo error en ningún log. Ese caso no comparte mensaje
   * con los demás.
   */
  const repartirAlCrear = async (widgetId: string, nombre: string): Promise<string | null> => {
    // Paso 2 — restringir. Si esto falla, el widget se queda privado (nació así): lo ve su autor y
    // nadie más. Es el fallo más benigno de los tres y aun así se dice.
    try {
      await accessAdapter!.savePolicy!(widgetId, { visibility: 'restricted' });
    } catch {
      return t('dashboard.builder.step5.partialNotRestricted', { nombre });
    }

    // Paso 3 — conceder, una llamada por persona. Se hacen TODAS aunque alguna falle: parar en la
    // primera dejaría un reparto a medias distinto en cada corrida, y sin decir cuál.
    const ok: string[] = [];
    const fallidos: string[] = [];
    for (const persona of personas) {
      try {
        await accessAdapter!.grant(widgetId, {
          subjectType: 'user',
          subjectId: persona.id,
          level: 'viewer',
        });
        ok.push(persona.nombre?.trim() || persona.email?.trim() || persona.id);
      } catch {
        fallidos.push(persona.nombre?.trim() || persona.email?.trim() || persona.id);
      }
    }

    if (fallidos.length === 0) return null;
    if (ok.length === 0) return t('dashboard.builder.step5.partialNobody', { nombre });
    return t('dashboard.builder.step5.partialSome', {
      nombre,
      ok: ok.join(', '),
      fallidos: fallidos.join(', '),
    });
  };

  /** Deja el asistente como recién abierto. Lo usan el guardado feliz y las dos salidas del aviso. */
  const limpiarAsistente = () => {
    setFormData({ ...INITIAL_FORM_DATA, is_internal: false });
    setStep(1);
    setTestSuccess(false);
    setTestError(null);
    setJsonPreview(null);
    setMappingConfig(null);
    setTotalRecords(0);
    setSelectedIndicator(null);
    setReparto('module');
    setPersonas([]);
    setAvisoReparto(null);
    setCreadoAMedias(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Para modo catálogo, construir field_mapping desde el indicador seleccionado
      let fieldMapping: Record<string, string> = {};
      let configSchema: Record<string, unknown> | undefined;
      let catalogFieldMappingObj: WidgetFieldMapping | undefined;

      if (isCatalogMode && selectedIndicator) {
        // Extraer el nombre del campo limpio (sin notación array [0].)
        const rawField = selectedIndicator.campo_valor.replace(/^\[\d+\]\./, '').replace(/\[.*?\]\./g, '');
        const rawDateField = selectedIndicator.campo_fecha
          ? selectedIndicator.campo_fecha.replace(/^\[\d+\]\./, '').replace(/\[.*?\]\./g, '')
          : undefined;

        // Si el campo contiene puntos (ruta anidada, ej: "bitcoin.usd"), usar rename
        // para que applyFieldMapping lo aplane a "value" usando getNestedValue (dot-notation).
        const hasNestedPath = rawField.includes('.');
        const canonicalField = hasNestedPath ? 'value' : rawField;

        catalogFieldMappingObj = {
          values: [canonicalField],
          aliases: { [canonicalField]: selectedIndicator.nombre },
          // rename: { widgetField: apiField } — "value" se lee de "bitcoin.usd" (dot-notation)
          ...(hasNestedPath ? { rename: { [canonicalField]: rawField } } : {}),
          fields: [
            { name: canonicalField, type: 'number' as const, label: selectedIndicator.nombre },
            ...(rawDateField ? [{ name: rawDateField, type: 'date' as const, label: 'Fecha' }] : []),
          ],
        };
        configSchema = { type: 'kpi', valueField: canonicalField, aggregation: 'last' };
      } else {
        fieldMapping = buildFieldMapping(mappingConfig);
        configSchema = mappingConfig ? { ...mappingConfig } as unknown as Record<string, unknown> : undefined;
      }

      if (isEditMode && editWidget) {
        // PATCH — actualizar widget existente
        const patch: Partial<WidgetDefinition> = {
          nombre: formData.nombre || editWidget.nombre,
          descripcion: formData.descripcion,
          categoria: formData.categoria as DomainCategory,
          tipo: formData.tipo as WidgetPresentationType,
          icono: formData.icono || editWidget.icono,
          cols_default: formData.cols_default,
          altura_default: formData.tipo === 'kpi' ? 1 : 2,
          endpoint_url: formData.endpoint_url,
          endpoint_method: formData.endpoint_method,
          endpoint_headers: Object.keys(formData.endpoint_headers ?? {}).length > 0
            ? formData.endpoint_headers
            : undefined,
          endpoint_params: Object.keys(formData.endpoint_params ?? {}).length > 0
            ? formData.endpoint_params
            : undefined,
          field_mapping: Object.keys(fieldMapping).length > 0 ? fieldMapping : undefined,
          config_schema: configSchema,
          refresh_policy: formData.refresh_policy || undefined,
          // 🔴 Los campos de EXPOSICION solo viajan si se pueden mover. Antes iba
          // `es_privado: isAdmin ? ... : true`, y eso tenia una consecuencia que no daba la cara:
          // un editor no-administrador que corrigiera un titulo mandaba `es_privado: true` sin
          // saberlo y volvia PRIVADO un widget de modulo, con lo que desaparecia del tablero de
          // todos los demas. El backend lo aceptaba porque no distinguia editar de exponer.
          //
          // 🔴 F3 — y AQUI TAMBIEN, que es la mitad que se olvida. Si la vertical se derivara solo
          // al crear, editar un widget y cambiarle la categoria lo dejaria archivado en un sitio y
          // visible en otro: exactamente la divergencia que F3 cierra, reabierta por la puerta de
          // atras. `verticalDeCategoria ?? roles actuales` conserva lo que hubiera cuando el host no
          // publica la pareja, en vez de vaciarlo sin que nadie lo pida.
          ...(puedeExponer
            ? {
                roles_permitidos: vertical ? [vertical.role!] : formData.roles_permitidos,
                es_privado: formData.es_privado,
                is_internal: resolverIsInternal(),
              }
            : {}),
        };
        const updated = await updateWidget(editWidget.widget_id, patch, fetcher);
        onSuccess(updated);
      } else {
        // POST — crear widget nuevo
        const tipo = isCatalogMode ? 'kpi' : formData.tipo as WidgetPresentationType;
        const widgetGenerado: Omit<WidgetDefinition, 'version'> = {
          widget_id: `custom_${crypto.randomUUID()}`,
          nombre: formData.nombre || selectedIndicator?.nombre || 'Widget Sin Nombre',
          descripcion: formData.descripcion || selectedIndicator?.descripcion || '',
          categoria: formData.categoria as DomainCategory,
          tipo,
          icono: isCatalogMode ? 'Hash' : (formData.icono || 'Layout'),
          // Sin autoridad para exponer, el backend fuerza privado y sin modulos: mandarlo asi evita
          // que la pantalla diga una cosa y la fila guardada otra.
          //
          // 🔴 F3 — con autoridad, la vertical es LA DE LA CATEGORIA y no se pregunta. El default
          // historico era `['admin']`, que no es una vertical de nadie: era el vocabulario cableado
          // del kit asomando por el valor inicial del formulario.
          roles_permitidos: puedeExponer && vertical ? [vertical.role!] : [],
          col_min: 2,
          col_max: 12,
          cols_default: formData.cols_default || 3,
          altura_default: tipo === 'table' ? 2 : 1,
          orden_default: 99,
          activo: true,
          endpoint_url: formData.endpoint_url,
          endpoint_method: formData.endpoint_method,
          endpoint_headers: Object.keys(formData.endpoint_headers ?? {}).length > 0
            ? formData.endpoint_headers
            : undefined,
          endpoint_params: Object.keys(formData.endpoint_params ?? {}).length > 0
            ? formData.endpoint_params
            : undefined,
          field_mapping: catalogFieldMappingObj ?? (Object.keys(fieldMapping).length > 0 ? fieldMapping : undefined),
          config_schema: configSchema,
          refresh_policy: formData.refresh_policy || (isCatalogMode ? 3600 : undefined),
          // 🔴 F4 — la ruta «solo a estas personas» nace PRIVADA, y luego se restringe.
          //
          // Son tres llamadas y no hay transaccion, asi que lo que importa es hacia donde falla cada
          // una. Naciendo `es_privado: false` (modulo) un fallo del segundo paso dejaria el widget
          // ABIERTO A TODA LA VERTICAL: el estado que nadie pidio, y silencioso. Naciendo privado, el
          // peor final de cada paso es «lo ve menos gente de la que queria», que se arregla abriendo
          // el dialogo de acceso. FALLA CERRANDO, a proposito.
          es_privado: puedeExponer ? (repartoAPersonas ? true : formData.es_privado) : true,
          is_internal: resolverIsInternal(),
        };
        const finalWidget = await createWidget(widgetGenerado, fetcher);

        if (repartoAPersonas) {
          const aviso = await repartirAlCrear(finalWidget.widget_id, widgetGenerado.nombre);
          if (aviso) {
            // 🔴 NO se cierra el asistente, y por eso NO se llama a `onSuccess`: el host lo usa para
            // refrescar Y CERRAR. Cerrar con el aviso puesto seria dejarlo sin sitio donde leerse,
            // que es la version educada de no decirlo. Se cierra cuando la persona lo acepta.
            setAvisoReparto(aviso);
            setCreadoAMedias(finalWidget);
            setIsSaving(false);
            return;
          }
        }

        onSuccess(finalWidget);
      }

      limpiarAsistente();
      onClose();
    } catch (error) {
      setTestError(error instanceof Error ? error.message : 'Error al guardar el widget');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  // Validaciones por paso
  const canAdvance = (() => {
    switch (currentStepId) {
      case 'dataSource':
        if (isCatalogMode) {
          return !!selectedIndicator && (!selectedIndicator.requiere_key || !!formData.catalog_api_key);
        }
        return !formData.endpoint_url || testSuccess;
      case 'presentation': return true;
      case 'mapping': return !jsonPreview || isMappingValid(mappingConfig);
      // 🔴 La CATEGORIA es obligatoria, igual que el nombre. Antes solo se exigia el nombre, y el
      // backend tampoco la pedia —medido: `categoria: ""` devolvia 201—, asi que un indicador podia
      // acabar sin ella y caer en el grupo «Otros» para siempre. No es una fuga, es desorden
      // permanente en el catalogo del tenant, y nadie se enteraba.
      //
      // El componente ya la rechaza (`CreateWidgetValidator` con `NotEmpty`, que desde 0.0.27 SI se
      // ejecuta). Esto es la mitad de UX: que no se pueda llegar al final para recibir un 400.
      case 'appearance': return !!formData.nombre?.trim() && !!formData.categoria?.trim();
      default: return true;
    }
  })();

  const validationHint = (() => {
    if (currentStepId === 'dataSource') {
      if (isCatalogMode && !selectedIndicator) return 'Selecciona un indicador del catálogo';
      if (isCatalogMode && selectedIndicator?.requiere_key && !formData.catalog_api_key)
        return `Ingresa tu ${selectedIndicator.key_nombre} para continuar`;
      if (!isCatalogMode && formData.endpoint_url && !testSuccess)
        return 'Prueba la conexión antes de continuar';
    }
    if (currentStepId === 'mapping' && jsonPreview && !isMappingValid(mappingConfig))
      return 'Completa los campos de mapeo obligatorios';
    if (currentStepId === 'appearance' && !formData.nombre?.trim())
      return 'El nombre del widget es obligatorio';
    if (currentStepId === 'appearance' && !formData.categoria?.trim())
      return 'Elige la categoría donde va a quedar el indicador';
    return '';
  })();

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 sm:p-6">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">

        {/* Cabecera */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {isEditMode ? `Editar widget: ${editWidget?.nombre}` : 'Constructor de Widgets'}
            </h2>
            <p className="text-sm text-gray-500">
              {isEditMode ? 'Modifica la configuración del widget' : 'Crea un nuevo indicador sin programar'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stepper Visual */}
        <div className="flex items-center justify-between px-10 py-4 border-b border-gray-100 bg-white">
          {steps.map((s, index) => (
            <div key={s.id} className="flex flex-col items-center relative z-10 w-full">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors duration-300 bg-white
                ${step === index + 1 ? 'border-blue-600 text-blue-600' : step > index + 1 ? 'border-green-500 bg-green-50 text-green-500' : 'border-gray-200 text-gray-400'}
              `}>
                {step > index + 1 ? <CheckCircle className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <span className={`text-xs mt-2 font-medium ${step === index + 1 ? 'text-blue-700' : 'text-gray-500'}`}>{s.title}</span>
              {index < steps.length - 1 && (
                <div className={`absolute top-5 left-[50%] right-[-50%] h-[2px] -z-10
                  ${step > index + 1 ? 'bg-green-400' : 'bg-gray-200'}
                `} />
              )}
            </div>
          ))}
        </div>

        {/* Contenido del Paso Actual */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-50/30">
          {/* Placeholder — nunca debería renderse porque currentStepId maneja todo */}
          {currentStepId === 'dataSource' && (
            <Step1DataSource
              formData={formData}
              testSuccess={testSuccess}
              isTesting={isTesting}
              testError={testError}
              totalRecords={totalRecords}
              allowedRoutes={allowedRoutes}
              isAdmin={isAdmin}
              showExternalEndpoints={showExternalEndpoints}
              onUpdateForm={updateForm}
              onTestConnection={handleTestConnection}
              onIndicatorSelect={(ind) => setSelectedIndicator(ind)}
            />
          )}
          {currentStepId === 'presentation' && (
            <Step3PresentationType
              selectedTipo={formData.tipo}
              onSelect={(tipo) => updateForm('tipo', tipo)}
            />
          )}
          {currentStepId === 'mapping' && (
            <Step2FieldMapping
              presentationType={formData.tipo}
              jsonPreview={jsonPreview}
              mappingConfig={mappingConfig}
              onMappingConfigChange={setMappingConfig}
              isCatalogMode={false}
              campoValorPrefill={formData.campo_valor || undefined}
              campoFechaPrefill={formData.campo_fecha || undefined}
            />
          )}
          {currentStepId === 'appearance' && (
            <Step4Appearance
              availableCategories={availableCategories}
              formData={formData}
              onUpdateForm={updateForm}
              indicatorUnidad={selectedIndicator?.unidad}
              indicatorBandera={selectedIndicator?.bandera}
              selectedIndicator={selectedIndicator}
              isCatalogMode={isCatalogMode}
            />
          )}
          {currentStepId === 'permissions' && puedeExponer && (
            <Step5Permissions
              esPrivado={formData.es_privado}
              onUpdatePrivacy={(isPrivate) => updateForm('es_privado', isPrivate)}
              vertical={vertical}
              reparto={reparto}
              onUpdateReparto={setReparto}
              personas={personas}
              onUpdatePersonas={setPersonas}
              searchPeople={buscarPersonas}
              puedeRepartirAlCrear={puedeRepartirAlCrear}
            />
          )}
        </div>

        {/*
          🔴 F4.4 — el final a medias, DICHO. El widget ya existe, asi que esto no es un error del
          guardado: es el estado en que quedo. Se pinta aqui y el asistente NO se cierra, porque
          cerrarlo seria dejar el aviso sin sitio donde leerse.
        */}
        {avisoReparto && (
          <div className="px-6 py-3 border-t border-amber-200 bg-amber-50 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-amber-900">{avisoReparto}</p>
              <div className="mt-2 flex gap-2">
                {/*
                  🔴 La salida que el aviso promete. Sin ella se le dice a alguien que su indicador
                  quedó a medias y se le deja buscarlo en el catálogo — el aviso sería un callejón.
                */}
                {onNeedsAccessDialog && creadoAMedias && (
                  <button
                    onClick={() => { const w = creadoAMedias; limpiarAsistente(); onNeedsAccessDialog(w); }}
                    className="px-3 py-1.5 text-xs font-semibold text-amber-900 bg-white border border-amber-300 rounded hover:bg-amber-100"
                  >
                    {t('dashboard.builder.step5.openAccessDialog')}
                  </button>
                )}
                <button
                  onClick={() => { const w = creadoAMedias; limpiarAsistente(); if (w) onSuccess(w); }}
                  className="px-3 py-1.5 text-xs font-medium text-amber-900 hover:underline"
                >
                  {t('dashboard.builder.step5.dismissPartial')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer Controles */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-white gap-4">
          <button
            onClick={() => setStep(s => Math.max(1, s - 1))}
            className={`px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors
              ${step === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'}
            `}
            disabled={step === 1}
          >
            <ArrowLeft className="w-4 h-4" /> Anterior
          </button>

          <span className="text-xs text-amber-600 flex-1 text-center">{validationHint}</span>

          {step < maxStep ? (
            <button
              onClick={() => setStep(s => Math.min(maxStep, s + 1))}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!canAdvance}
            >
              Siguiente <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={isSaving || !formData.nombre}
              className="px-6 py-2.5 bg-green-600 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-green-700 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isSaving ? 'Guardando...' : isEditMode ? 'Actualizar Widget' : 'Finalizar y Crear Widget'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
